import { useMemo, useState } from "react";
import { CurrencyText, TranslatedText } from "@/components/common/text";
import { cn } from "@/utils/css";
import { ChartHoverTooltip } from "./ChartHoverTooltip";
import { rateScale } from "./useRateDistributionFit";

const CURVE_SAMPLES = 80;
const SNAP_RADIUS_PX = 3;
const VIEW_W = 285;
const VIEW_H = 135;
const X_TICKS = 5;

const toY = (p) => (1 - p) * VIEW_H;

// CDF through the buy rate quantiles: percentile (y) vs $/mi (x). The fixed dot marks the buy
// rate; hover shows the price and percentile under the cursor.
export const BuyRateCdfChart = ({ cdf, buyRpm, priceScale }) => {
    const [hover, setHover] = useState(null); // { rpm, p, snapped, width }

    const geometry = useMemo(() => {
        if (!cdf || buyRpm == null) return null;
        const scale = rateScale(cdf.lo, cdf.hi, VIEW_W);
        const curve = Array.from({ length: CURVE_SAMPLES + 1 }, (_, i) => {
            const rpm = scale.sampleRpm(i, CURVE_SAMPLES);
            return `${i === 0 ? "M" : "L"} ${scale.toX(rpm).toFixed(2)} ${toY(cdf.at(rpm)).toFixed(2)}`;
        }).join(" ");
        const area = `${curve} L ${VIEW_W} ${VIEW_H} L 0 ${VIEW_H} Z`;
        // The CDF passes through the p50 knot (the buy rate), so the dot sits at 50%.
        const clampedBuy = Math.min(scale.hi, Math.max(scale.lo, buyRpm));
        const buy = { rpm: clampedBuy, p: 0.5 };
        const ticks = Array.from({ length: X_TICKS }, (_, i) => scale.sampleRpm(i, X_TICKS - 1).toFixed(2));
        return { scale, area, buy, ticks };
    }, [cdf, buyRpm]);

    if (!geometry) return null;
    const { pointMass } = cdf;

    const handleMouseMove = (e) => {
        if (pointMass) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const { scale, buy } = geometry;
        const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        // Snap only when the pointer is on the dot itself, so readings along the curve stay continuous.
        const dx = (frac - scale.toXFrac(buy.rpm)) * rect.width;
        const dy = e.clientY - rect.top - toY(buy.p);
        const snapped = dx * dx + dy * dy <= SNAP_RADIUS_PX * SNAP_RADIUS_PX;
        if (snapped) {
            if (!hover?.snapped) setHover({ rpm: buy.rpm, p: buy.p, snapped: true, width: rect.width });
            return;
        }
        const rpm = scale.lo + frac * (scale.hi - scale.lo);
        setHover({ rpm, p: cdf.at(rpm), snapped: false, width: rect.width });
    };

    const pctPos = (rpm, p) => ({
        left: `${geometry.scale.toXFrac(rpm) * 100}%`,
        top: `${(1 - p) * 100}%`,
    });

    const buyX = geometry.scale.toX(geometry.buy.rpm);
    const buyY = toY(geometry.buy.p);

    const crosshair = (x, y, className) => (
        <g className={className}>
            <line x1="0" y1={y} x2={x} y2={y} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1={x} y1={y} x2={x} y2={VIEW_H} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        </g>
    );

    return (
        <div className={cn("flex flex-col", pointMass && "opacity-40")}>
            <div className="flex w-full flex-row gap-1">
                <div className="flex h-[135px] w-[26px] shrink-0 flex-col justify-between text-xs/[19px] text-cm-p-text-secondary">
                    <span>95%</span>
                    <span>50%</span>
                    <span>5%</span>
                </div>
                <div
                    className="relative h-[135px] min-w-0 grow"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setHover(null)}
                >
                    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                        {pointMass ? (
                            <line
                                x1="0"
                                y1={VIEW_H / 2}
                                x2={VIEW_W}
                                y2={VIEW_H / 2}
                                className="stroke-cm-p-outline"
                                strokeWidth="1"
                                vectorEffect="non-scaling-stroke"
                            />
                        ) : (
                            <>
                                <path d={geometry.area} className="fill-cm-p-surface-container" />
                                {crosshair(buyX, buyY, "stroke-cm-p-accent-200 opacity-60")}
                                {hover && !hover.snapped
                                    ? crosshair(geometry.scale.toX(hover.rpm), toY(hover.p), "stroke-cm-p-text-secondary opacity-20")
                                    : null}
                            </>
                        )}
                    </svg>
                    {!pointMass ? (
                        <>
                            {!hover || hover.snapped ? (
                                <div
                                    className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cm-p-accent-600"
                                    style={pctPos(geometry.buy.rpm, geometry.buy.p)}
                                />
                            ) : (
                                <div
                                    className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cm-p-text-secondary"
                                    style={pctPos(hover.rpm, hover.p)}
                                />
                            )}
                            {hover ? (
                                <ChartHoverTooltip
                                    x={geometry.scale.toXFrac(hover.rpm) * hover.width}
                                    y={(1 - hover.p) * VIEW_H}
                                    containerWidth={hover.width}
                                    className={hover.snapped ? "text-cm-p-on-container-green" : "text-cm-p-text-secondary"}
                                >
                                    <div className="flex flex-row items-center gap-2 leading-normal">
                                        {priceScale != null ? (
                                            <CurrencyText
                                                num={hover.rpm * priceScale}
                                                round
                                                className={cn(
                                                    "text-sm font-medium leading-normal",
                                                    hover.snapped ? "text-cm-p-on-container-green" : "text-cm-p-text-primary",
                                                )}
                                            />
                                        ) : null}
                                        <span className={cn("text-xs/[18px]", hover.snapped && "opacity-60")}>
                                            {hover.rpm.toFixed(2)}/mi
                                        </span>
                                    </div>
                                    <div className={cn("text-xs/[19px]", hover.snapped && "text-cm-p-text-primary opacity-60")}>
                                        {Math.round(hover.p * 100)} <TranslatedText>percentile</TranslatedText>
                                    </div>
                                </ChartHoverTooltip>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-row justify-between pl-[30px] pt-1 text-xs/[19px] text-cm-p-text-secondary">
                {geometry.ticks.map((t) => (
                    <span key={t}>{t}</span>
                ))}
            </div>
        </div>
    );
};